import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import uuid from 'react-uuid';
import {
  DailyNavReportColumnMapping,
  DailyNavReportMappingField,
  DailyNavReportTemplateUploadResponse,
  FirmDailyNavConfigUpdate,
  FirmDailyNavConfigVersion,
} from 'api';
import {
  DAILY_NAV_DEFAULT_LONG_WINDOW_SESSIONS,
  DAILY_NAV_DEFAULT_MONITORING_MODE,
  DAILY_NAV_DEFAULT_REPORT_SEND_TIME,
  DAILY_NAV_DEFAULT_REPORTING_CURRENCY,
  DAILY_NAV_DEFAULT_SFTP_PORT,
  DAILY_NAV_DEFAULT_SHORT_WINDOW_SESSIONS,
  DAILY_NAV_DEFAULT_TRADING_MARKET,
  DAILY_NAV_THRESHOLD_KEYS,
  DailyNavMonitoringMode,
  DailyNavReportingCurrency,
  DailyNavThresholdKey,
  DailyNavTradingMarket,
  formatDailyNavReportSendTime,
  fractionToPercent,
  isShortWindowBeforeLongWindow,
  isValidReportSendTime,
  isValidThresholdPercent,
  isValidWindowSessions,
  percentToFraction,
} from 'common/constants/dailyNav';
import { areDailyNavReportMappingsComplete } from 'pages/Firms/pages/DailyNavSettings/DailyNavTemplateMapper';
import { DailyNavSetupStep } from '../dailyNavSetup.utils';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidEmail = (email: string) => EMAIL_PATTERN.test(email.trim());

export type RecipientRow = { email: string; key: string };

// Blank rows are ignored rather than failing validation with nothing marked red.
const recipientEmails = (rows: RecipientRow[]) => rows.map(row => row.email.trim()).filter(Boolean);
const areRecipientsValid = (rows: RecipientRow[]) => recipientEmails(rows).every(isValidEmail);

export const editRecipientRows = (rows: RecipientRow[], index: number, email: string) =>
  rows.map((row, rowIndex) => (rowIndex === index ? { ...row, email } : row));

export type FirmSetupDraft = {
  tradingMarket: string;
  monitoringMode: DailyNavMonitoringMode;
  reportSendTime: string;
  shortWindowSessions: string;
  longWindowSessions: string;
  thresholds: Record<DailyNavThresholdKey, string>;
  recipients: RecipientRow[];
  reportTemplateId: number | null;
  reportTemplateFilename: string;
  reportColumnMappings: DailyNavReportColumnMapping[];
  reportMappingFields: DailyNavReportMappingField[];
  ftpHost: string;
  ftpPort: string;
  ftpPath: string;
  ftpUsername: string;
  deliveryRecipients: RecipientRow[];
};

const toRecipientRows = (emails: unknown) =>
  (Array.isArray(emails) ? (emails as string[]) : []).map(email => ({ email, key: uuid() }));

// Same seeding as Daily NAV Settings: an unsaved firm gets the API defaults for
// market, mode, windows and send time, but blank thresholds so each is chosen.
const draftFromConfig = (config?: FirmDailyNavConfigVersion | null): FirmSetupDraft => {
  const saved = config?.id ? config : null;
  return {
    tradingMarket: saved ? (saved.trading_calendar ?? '') : DAILY_NAV_DEFAULT_TRADING_MARKET,
    monitoringMode: saved?.monitoring_mode ?? DAILY_NAV_DEFAULT_MONITORING_MODE,
    reportSendTime: saved
      ? formatDailyNavReportSendTime(saved.report_send_time)
      : DAILY_NAV_DEFAULT_REPORT_SEND_TIME,
    shortWindowSessions: String(saved ? (saved.short_window_sessions ?? '') : DAILY_NAV_DEFAULT_SHORT_WINDOW_SESSIONS),
    longWindowSessions: String(saved ? (saved.long_window_sessions ?? '') : DAILY_NAV_DEFAULT_LONG_WINDOW_SESSIONS),
    thresholds: Object.fromEntries(
      DAILY_NAV_THRESHOLD_KEYS.map(key => [key, saved ? fractionToPercent(saved[key]) : ''])
    ) as Record<DailyNavThresholdKey, string>,
    recipients: saved ? toRecipientRows(saved.notification_recipients) : [],
    reportTemplateId: config?.report_template ?? null,
    reportTemplateFilename: config?.report_template_filename ?? '',
    reportColumnMappings: config?.report_column_map ?? [],
    reportMappingFields: config?.report_mapping_fields ?? [],
    ftpHost: saved?.ftp_host ?? '',
    ftpPort: String(saved?.ftp_port ?? DAILY_NAV_DEFAULT_SFTP_PORT),
    ftpPath: saved?.ftp_path ?? '',
    ftpUsername: saved?.ftp_username ?? '',
    deliveryRecipients: saved ? toRecipientRows(saved.delivery_recipients) : [],
  };
};

const comparable = (draft: FirmSetupDraft) =>
  JSON.stringify({
    ...draft,
    recipients: recipientEmails(draft.recipients),
    deliveryRecipients: recipientEmails(draft.deliveryRecipients),
    reportTemplateFilename: undefined,
    reportMappingFields: undefined,
  });

export const useFirmSetupForm = (config?: FirmDailyNavConfigVersion | null) => {
  const [draft, setDraft] = useState<FirmSetupDraft>(() => draftFromConfig(config));
  const baseline = useMemo(() => draftFromConfig(config), [config]);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  // What the current draft was seeded from, so a background refetch never replaces edits.
  const seededFromRef = useRef(baseline);
  const reseedNextRef = useRef(false);

  useEffect(() => {
    if (reseedNextRef.current || comparable(draftRef.current) === comparable(seededFromRef.current)) {
      reseedNextRef.current = false;
      seededFromRef.current = baseline;
      setDraft(baseline);
    }
  }, [baseline]);

  // Discard: back to what is saved, so a leave-prompt "Discard" leaves no edits behind.
  const reset = useCallback(() => {
    seededFromRef.current = baseline;
    setDraft(baseline);
  }, [baseline]);

  // True before this form's own save, so the saved config replaces the draft; false if it fails.
  const acceptNextConfig = useCallback((accept = true) => {
    reseedNextRef.current = accept;
  }, []);

  const update = useCallback(<K extends keyof FirmSetupDraft>(key: K, value: FirmSetupDraft[K]) => {
    setDraft(current => ({ ...current, [key]: value }));
  }, []);

  const applyUploadedTemplate = useCallback((uploaded: DailyNavReportTemplateUploadResponse) => {
    setDraft(current => ({
      ...current,
      reportTemplateId: uploaded.id,
      reportTemplateFilename: uploaded.filename,
      reportMappingFields: uploaded.mapping_fields,
      reportColumnMappings: uploaded.columns.map((columnName, columnIndex) => ({
        column_index: columnIndex,
        column_name: columnName,
        kind: DailyNavReportColumnMapping.kind.FIELD,
      })),
    }));
  }, []);

  const isWindowOrderValid =
    isValidWindowSessions(draft.shortWindowSessions) &&
    isValidWindowSessions(draft.longWindowSessions) &&
    isShortWindowBeforeLongWindow(draft.shortWindowSessions, draft.longWindowSessions);

  const stepValidity: Partial<Record<DailyNavSetupStep, boolean>> = {
    [DailyNavSetupStep.MARKET]:
      Boolean(draft.tradingMarket) &&
      isValidReportSendTime(draft.reportSendTime) &&
      areRecipientsValid(draft.recipients),
    [DailyNavSetupStep.THRESHOLDS]:
      isWindowOrderValid && DAILY_NAV_THRESHOLD_KEYS.every(key => isValidThresholdPercent(draft.thresholds[key])),
    [DailyNavSetupStep.DELIVERY]:
      areRecipientsValid(draft.deliveryRecipients) &&
      areDailyNavReportMappingsComplete(draft.reportTemplateId, draft.reportColumnMappings),
  };

  // Against the seeded defaults on an unsaved firm, so opening the wizard is not an edit.
  const isDirty = comparable(draft) !== comparable(baseline);

  const toPayload = (): FirmDailyNavConfigUpdate => ({
    ...(Object.fromEntries(
      DAILY_NAV_THRESHOLD_KEYS.map(key => [key, percentToFraction(draft.thresholds[key])])
    ) as Record<DailyNavThresholdKey, string>),
    notification_recipients: recipientEmails(draft.recipients),
    // Generated read and write enums are distinct nominal types.
    trading_calendar: draft.tradingMarket as DailyNavTradingMarket,
    monitoring_mode: draft.monitoringMode,
    // `||` not `??`: an unsaved config serializes the currency as "".
    reporting_currency: (config?.reporting_currency || DAILY_NAV_DEFAULT_REPORTING_CURRENCY) as DailyNavReportingCurrency,
    short_window_sessions: Number(draft.shortWindowSessions),
    long_window_sessions: Number(draft.longWindowSessions),
    report_send_time: draft.reportSendTime,
    ...(draft.reportTemplateId !== null
      ? { report_template: draft.reportTemplateId, report_column_map: draft.reportColumnMappings }
      : {}),
    ftp_host: draft.ftpHost || null,
    ftp_port: Number(draft.ftpPort) || DAILY_NAV_DEFAULT_SFTP_PORT,
    ftp_path: draft.ftpPath || null,
    ftp_username: draft.ftpUsername || null,
    delivery_recipients: recipientEmails(draft.deliveryRecipients),
  });

  return {
    draft,
    update,
    applyUploadedTemplate,
    isWindowOrderValid,
    stepValidity,
    isDirty,
    toPayload,
    acceptNextConfig,
    reset,
  };
};
